import { User } from "@fableous/core/model/User";
import { useCallback, useEffect } from "react";
import ApiClient from "../libs/ApiClient";
import { useObservableReducer } from "./useObservableReducer";
import { UserAction, userStore } from "../libs/userLib";
import { GlobalEventEmitter } from "../libs/eventLib";
import logger from "../libs/errorLib";
import { useSessionState } from "./useSessionState";

let hasRequestedUser = false;

export function useUserState() {
	const [state, dispatch] = useObservableReducer<User | undefined, UserAction>(userStore);
	const {session} = useSessionState();

	/**
	 * Request an update to the user record.
	 * @param force will force the user record to be updated in state (otherwise it won't if the userID is the same)
	 */
	const requestUpdate = useCallback(
		async (force: boolean = false) => {
			if (hasRequestedUser) return;
			logger.debug("Requesting user record update, force: " + force);
			const apiClient = new ApiClient("api");
			let newUser: User | undefined = undefined;
			hasRequestedUser = true;
			try {
				newUser = await apiClient.get<User>(`/user`, undefined, false);
			} catch (e) {
				GlobalEventEmitter.emit("error", e);
			}
			if (newUser) dispatch({ type: "setUser", value: { user: newUser, force } });
			hasRequestedUser = false;
		},
		[dispatch]
	);

	useEffect(()=>{
		if(session.isAuthenticated) void requestUpdate();
	},[session.isAuthenticated, requestUpdate]);

	return {
		user: state,
		setUser: (user: User | undefined, force?: boolean) => dispatch({ type: "setUser", value: { user, force } }),
		requestUpdate,
	};
}
